<template>
  <div v-if="!state.calendarData.priceCalendars?.length" class="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
    <Empty desc="" :show-btn="true" @empty-event="fetchPriceCalendar" />
  </div>
  <div v-else p="x-5">
    <CalendarRoot
      v-slot="{ weekDays, grid }"
      v-model="selectDate"
      :min-value="parseDate(dayjs(minDate).format('YYYY-MM-DD'))"
      :max-value="parseDate(dayjs(maxDate).format('YYYY-MM-DD'))"
      :number-of-months="2"
      :is-date-disabled="isDateDisabled"
      weekday-format="short"
    >
      <div class="flex space-x-10">
        <CalendarGrid
          v-for="(month, monthIndex) in grid"
          :key="month.value.toString()"
          class="border-collapse select-none space-y-1"
        >
          <CalendarGridHead>
            <div flex="~ items-center justify-center" h="9" class="relative" m="b-5 l-2">
              <CalendarPrev
                v-if="monthIndex === 0"
                class="absolute left-0 h-9 w-9 rounded-md data-[disabled]:cursor-not-allowed hover:bg-[#121212] data-[disabled]:text-black/30 hover:text-white data-[disabled]:hover:bg-transparent data-[disabled]:hover:text-black/30"
                cursor="pointer"
              >
                <NuxtIcon name="my-icon:arrow-right" class="align-middle" rotate="180" text="2xl" />
              </CalendarPrev>
              <div text="#121212 xl" font="600" m="x-1">
                {{ dayjs(month.value).format('MMM YYYY') }}
              </div>
              <CalendarNext
                v-if="monthIndex === 1"
                class="absolute right-0 h-9 w-9 rounded-md data-[disabled]:cursor-not-allowed hover:bg-[#121212] data-[disabled]:text-black/30 hover:text-white data-[disabled]:hover:bg-transparent data-[disabled]:hover:text-black/30"
                cursor="pointer"
              >
                <NuxtIcon name="my-icon:arrow-right" class="align-middle" text="2xl" />
              </CalendarNext>
            </div>
            <div text="#121212 xl" font="600" m="b-5 l-4">
              {{ dayjs(month.value).format('MMM YYYY') }}
            </div>
            <CalendarGridRow class="mb-1 w-full flex justify-between">
              <CalendarHeadCell
                v-for="day in weekDays"
                :key="day"
                font="400"
                m="b-2"
                class="w-15 rounded-md text-121212 text-sm"
              >
                {{ day }}
              </CalendarHeadCell>
            </CalendarGridRow>
          </CalendarGridHead>
          <CalendarGridBody>
            <CalendarGridRow
              v-for="(weekDates, index) in month.rows"
              :key="`weekDate-${index}`"
              class="w-full flex"
            >
              <CalendarCell
                v-for="weekDate in weekDates"
                :key="weekDate.toString()"
                :date="weekDate"
              >
                <CalendarCellTrigger
                  v-slot="{ dayValue }"
                  :day="weekDate"
                  :month="month.value"
                  h="15"
                  w="15"
                  flex="~ col items-center justify-center"
                  b="~ transparent"
                  text="base 121212"
                  cursor="pointer"
                  class="group my-0.5 whitespace-nowrap rounded-full bg-transparent font-normal outline-none data-[unavailable]:pointer-events-none data-[outside-view]:invisible before:absolute before:top-[5px] before:hidden before:h-1 before:w-1 data-[disabled]:cursor-not-allowed hover:border-black before:rounded-full before:bg-white data-[selected]:bg-black data-[disabled]:text-black/30 data-[selected]:text-white data-[unavailable]:text-black/30 data-[selected]:font-medium data-[unavailable]:line-through data-[today]:before:block data-[selected]:before:bg-white"
                >
                  <div>{{ dayValue }}</div>
                  <div text="xs #868686" class="group-data-[selected]:text-white">
                    {{ formatPrice(weekDate) }}
                  </div>
                </CalendarCellTrigger>
              </CalendarCell>
            </CalendarGridRow>
          </CalendarGridBody>
        </CalendarGrid>
      </div>
    </CalendarRoot>
    <div p="t-4 x-4">
      <template v-if="state.calendarData?.packages?.length !== 1">
        <div text="lg black" m="b-4" font="600">
          {{ t('key.ticket.options') }}
        </div>
        <el-radio-group v-model="state.selected.packageId" class="az-package-radio">
          <el-radio
            v-for="item in state.calendarData?.packages" :key="item.packageId"
            class="mb-3"
            :border="true"
            :disabled="isDisabledTicketOption(item)"
            :label="item.packageName"
            :value="item.packageId"
            @click.prevent="() => {
              if (isDisabledTicketOption(item)) return

              state.selected.packageId === item.packageId ? state.selected.packageId = '' : state.selected.packageId = item.packageId || ''
            }"
          />
        </el-radio-group>
      </template>
    </div>
    <div flex="~ items-center justify-end" p="y-2">
      <ElButton
        size="large"
        @click="() => {
          state.isShow = false
        }"
      >
        <span text="base" font="600">{{ t('key.common.button.cancel') }}</span>
      </ElButton>
      <ElButton
        type="primary"
        :disabled="!state.selected.date || !state.selected.packageId"
        size="large"
        @click="onConfirm(attrId)"
      >
        <span text="base" font="600">{{ t('key.business.select.ticket.apply') }}</span>
      </ElButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { type DateValue, parseDate } from '@internationalized/date'
import type { CalendarRootProps } from 'radix-vue'
import { toDate } from 'radix-vue/date'

defineProps({
  attrId: {
    type: Number,
  },
})

const { t } = useI18n()
const {
  state,
  getters: { minDate, maxDate },
  actions: {
    isDisabledDate,
    isDisabledTicketOption,
    onConfirm,
    fetchPriceCalendar,
    formatPrice,
  },
} = usePriceCalendarState()
const dayjs = useDayjs()

await fetchPriceCalendar()

const selectDate = computed({
  set(value: DateValue) {
    if (!value) {
      return
    }
    state.value.selected.date = dayjs(toDate(value)).format('YYYY-MM-DD')
  },
  get() {
    return parseDate(state.value.selected.date)
  },
})

const isDateDisabled: CalendarRootProps['isDateDisabled'] = (value: DateValue) => {
  const date = state.value.calendarData?.priceCalendars?.find(i => i.date === dayjs(toDate(value)).format('YYYY-MM-DD'))
  return isDisabledDate(date)
}
</script>

<style lang="scss">
.az-package-radio {
  .el-radio {
    height: auto;
  }
  .el-radio__input {
    display: none;
  }
  .el-radio__label {
    padding-left: 0;
    color: #777474;
    font-size: 14px;
  }
  .el-radio.is-bordered {
    border: 1.5px solid #00000014;
    border-radius: 25px;
    padding: 6px 16px;
    margin-right: 16px;
    cursor: pointer;

    &.is-disabled {
      border-color: #D8D8D8;
      color: #D8D8D8;
      cursor: not-allowed;
    }

    &.is-checked {
      background-color: var(--tc-color-primary9);
      color: var(--tc-color-primary);
      border-color: var(--tc-color-primary);
    }
  }
}
</style>
